/**
 * Persist FileSystemDirectoryHandle (IndexedDB) so "Save project" updates the same folder
 * without re-picking, and keep version snapshots under versions/.
 */

import {
  canUseDirectoryPicker,
  sanitizeProjectFolderName,
  downloadVideoProjectJson,
  formatVideoProjectLanguageLabel,
  parseVideoProjectDocument,
} from '@/lib/videoEditorProjectFile';

const DB_NAME = 'twinTalesVideoEditorProjects';
const DB_VERSION = 1;
const STORE = 'projectLocations';

const VERSIONS_DIR = 'versions';
const MANIFEST_FILE = 'manifest.json';
const PROJECT_FILE = 'project.json';

function pad5(n) {
  return String(Math.max(0, Math.floor(n))).padStart(5, '0');
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'storyId' });
      }
    };
  });
}

export async function getPersistedProjectFolder(storyId) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(storyId);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function setPersistedProjectFolder(storyId, record) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({ storyId, ...record });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function clearPersistedProjectFolder(storyId) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(storyId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Read-only: never prompt during editor load (avoids hanging on a hidden permission dialog). */
async function ensureReadable(directoryHandle) {
  const opts = { mode: 'read' };
  try {
    return (await directoryHandle.queryPermission?.(opts)) === 'granted';
  } catch {
    return false;
  }
}

async function ensureWritable(directoryHandle) {
  const opts = { mode: 'readwrite' };
  try {
    if ((await directoryHandle.queryPermission?.(opts)) === 'granted') return true;
    if ((await directoryHandle.requestPermission?.(opts)) === 'granted') return true;
  } catch {
    return false;
  }
  return false;
}

/** Read root `project.json` from the linked folder (same doc as last save), if any. */
export async function readLinkedRootProjectFile(storyId) {
  if (!storyId) return null;
  const persisted = await getPersistedProjectFolder(storyId);
  if (!persisted?.directoryHandle) return null;
  const ok = await ensureReadable(persisted.directoryHandle);
  if (!ok) return null;
  try {
    const fh = await persisted.directoryHandle.getFileHandle(PROJECT_FILE);
    const file = await fh.getFile();
    const text = await file.text();
    const parsed = parseVideoProjectDocument(text);
    if (!parsed.ok) return null;
    if (parsed.doc.storyId !== storyId) return null;
    return parsed.doc;
  } catch {
    return null;
  }
}

/** Non-blocking read for editor load (never waits on folder permission prompts). */
export function readLinkedRootProjectFileWithTimeout(storyId, ms = 2500) {
  return Promise.race([
    readLinkedRootProjectFile(storyId),
    new Promise((resolve) => {
      setTimeout(() => resolve(null), ms);
    }),
  ]);
}

async function readManifest(versionsDir) {
  try {
    const fh = await versionsDir.getFileHandle(MANIFEST_FILE);
    const file = await fh.getFile();
    const m = JSON.parse(await file.text());
    if (!Array.isArray(m.entries)) m.entries = [];
    if (typeof m.nextIndex !== 'number' || m.nextIndex < 1) {
      const max = m.entries.reduce((acc, e) => Math.max(acc, e.index || 0), 0);
      m.nextIndex = max + 1 || 1;
    }
    return m;
  } catch {
    return { schema: 1, nextIndex: 1, entries: [] };
  }
}

async function writeTextFile(dirHandle, name, text) {
  const fh = await dirHandle.getFileHandle(name, { create: true });
  const w = await fh.createWritable();
  await w.write(text);
  await w.close();
}

/**
 * Save: reuse linked folder when possible; otherwise directory picker.
 * Writes versions/vNNNNN.json, updates versions/manifest.json, overwrites project.json.
 */
export async function saveProjectToLinkedOrNewFolder(storyId, doc, folderName) {
  const safe = sanitizeProjectFolderName(folderName);
  const langCode =
    doc?.story?.language != null && String(doc.story.language).trim()
      ? String(doc.story.language).trim().toLowerCase().slice(0, 8)
      : '';
  const langLabel = langCode ? formatVideoProjectLanguageLabel(langCode) : '';
  const langFileSuffix = langCode ? `-${langCode.replace(/[^a-z0-9_-]/gi, '')}` : '';

  if (!canUseDirectoryPicker()) {
    downloadVideoProjectJson(
      doc,
      `${safe}-v-${Date.now()}${langFileSuffix}.twin-tales-project.json`,
    );
    return { mode: 'download', folderName: safe, versionIndex: null };
  }

  let projectDir = null;
  const persisted = await getPersistedProjectFolder(storyId);

  if (persisted?.directoryHandle) {
    const ok = await ensureWritable(persisted.directoryHandle);
    if (ok) {
      projectDir = persisted.directoryHandle;
    } else {
      await clearPersistedProjectFolder(storyId);
    }
  }

  if (!projectDir) {
    const parent = await window.showDirectoryPicker({ mode: 'readwrite' });
    projectDir = await parent.getDirectoryHandle(safe, { create: true });
    await setPersistedProjectFolder(storyId, {
      folderName: safe,
      directoryHandle: projectDir,
    });
  }

  const json = `${JSON.stringify(doc, null, 2)}\n`;
  const versionsDir = await projectDir.getDirectoryHandle(VERSIONS_DIR, { create: true });
  const manifest = await readManifest(versionsDir);
  const index = manifest.nextIndex;
  manifest.nextIndex = index + 1;
  const fileName = `v${pad5(index)}.json`;
  manifest.entries.push({
    index,
    file: fileName,
    savedAt: doc.savedAt || new Date().toISOString(),
    label: langLabel || langCode || '',
    language: langCode || undefined,
  });

  await writeTextFile(versionsDir, fileName, json);
  await writeTextFile(versionsDir, MANIFEST_FILE, `${JSON.stringify(manifest, null, 2)}\n`);
  await writeTextFile(projectDir, PROJECT_FILE, json);

  return {
    mode: 'folder',
    folderName: persisted?.folderName || safe,
    versionIndex: index,
    linked: true,
  };
}

/** List saved versions from the linked project folder (newest first). */
export async function fetchProjectVersionList(storyId) {
  const persisted = await getPersistedProjectFolder(storyId);
  if (!persisted?.directoryHandle) {
    return { linked: false, folderName: null, entries: [] };
  }
  const ok = await ensureReadable(persisted.directoryHandle);
  if (!ok) {
    return { linked: false, folderName: persisted.folderName, entries: [], permissionDenied: true };
  }
  try {
    const versionsDir = await persisted.directoryHandle.getDirectoryHandle(VERSIONS_DIR);
    const manifest = await readManifest(versionsDir);
    const entries = [...manifest.entries].sort((a, b) => (b.index || 0) - (a.index || 0));
    return { linked: true, folderName: persisted.folderName, entries };
  } catch {
    return { linked: true, folderName: persisted.folderName, entries: [] };
  }
}

export async function readProjectVersionFile(storyId, fileName) {
  if (!/^v\d{5}\.json$/.test(fileName)) {
    throw new Error('Invalid version file name');
  }
  const persisted = await getPersistedProjectFolder(storyId);
  if (!persisted?.directoryHandle) throw new Error('No linked project folder');
  const ok = await ensureReadable(persisted.directoryHandle);
  if (!ok) throw new Error('Folder permission denied');
  const versionsDir = await persisted.directoryHandle.getDirectoryHandle(VERSIONS_DIR);
  const fh = await versionsDir.getFileHandle(fileName);
  const file = await fh.getFile();
  return file.text();
}

function pinnedVersionStorageKey(editorId) {
  return `tt-editor-pinned-version:${editorId}`;
}

/** Persist which cloud/local project version the user restored (survives tab refresh). */
export function loadPinnedEditorVersion(editorId) {
  if (!editorId) return null;
  try {
    const raw = sessionStorage.getItem(pinnedVersionStorageKey(editorId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.cloudSnapshotId) {
      return { cloudSnapshotId: String(parsed.cloudSnapshotId) };
    }
    if (parsed?.localVersionFile) {
      return { localVersionFile: String(parsed.localVersionFile) };
    }
    return null;
  } catch {
    return null;
  }
}

export function savePinnedEditorVersion(editorId, pin) {
  if (!editorId) return;
  if (pin?.cloudSnapshotId) {
    sessionStorage.setItem(
      pinnedVersionStorageKey(editorId),
      JSON.stringify({ cloudSnapshotId: String(pin.cloudSnapshotId) }),
    );
    return;
  }
  if (pin?.localVersionFile) {
    sessionStorage.setItem(
      pinnedVersionStorageKey(editorId),
      JSON.stringify({ localVersionFile: String(pin.localVersionFile) }),
    );
    return;
  }
  sessionStorage.removeItem(pinnedVersionStorageKey(editorId));
}

export function clearPinnedEditorVersion(editorId) {
  if (!editorId) return;
  sessionStorage.removeItem(pinnedVersionStorageKey(editorId));
}
